import { useTranslation } from "react-i18next";

import { EmptyState } from "../../../components/ui";
import type { MonitoringUnitRow } from "../hooks/useMonitoringCenter";
import type { CentinelaCaseView } from "../services/mapCentinelaCase";
import { formatAgo } from "../services/formatWatchTime";
import { WATCH_STATUS_MAP_COLORS } from "../theme";
import TabLoader from "./TabLoader";

const URGENCY_BORDERS = {
  critica: "border-error",
  alta: "border-error",
  media: "border-warning",
  baja: "border-info",
} as const;

const URGENCY_TEXT = {
  critica: "text-error",
  alta: "text-error",
  media: "text-warning",
  baja: "text-info",
} as const;

export interface MonitoringFeedProps {
  cases: CentinelaCaseView[];
  labelOfCase: (item: CentinelaCaseView) => string;
  onSelectCase: (item: CentinelaCaseView) => void;
  liveOf?: (deviceId: string) => MonitoringUnitRow | null;
  activeCaseId?: string | null;
  loading?: boolean;
  unavailable?: boolean;
  /** Pliega el panel. Es el mismo interruptor que el botón «Rastro del día»
   *  del mapa: las dos son la misma intención, «quítame el modo análisis». */
  onOcultar?: () => void;
}

export default function MonitoringFeed({
  cases,
  labelOfCase,
  onSelectCase,
  liveOf,
  activeCaseId = null,
  loading = false,
  unavailable = false,
  onOcultar,
}: MonitoringFeedProps) {
  const { t } = useTranslation("common");

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-card">
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
        {onOcultar ? (
          // Plegar desde el propio panel, además del botón del mapa: quien lo
          // quiere fuera está mirando el panel, no el mapa, y obligarlo a
          // cruzar la pantalla para cerrarlo es pedirle que recuerde dónde
          // estaba el interruptor.
          <button
            type="button"
            onClick={onOcultar}
            aria-label={t("monitoreoIa.monitoringCenter.hideFeed", {
              defaultValue: "Ocultar inteligencia en vivo",
            })}
            title={t("monitoreoIa.monitoringCenter.hideFeed", {
              defaultValue: "Ocultar inteligencia en vivo",
            })}
            className="shrink-0 text-content-faint transition-colors hover:text-content"
          >
            <i className="ri-contract-right-line text-sm" aria-hidden="true" />
          </button>
        ) : null}
        <span className="font-mono text-[9.5px] uppercase tracking-widest text-content-muted">
          {t("monitoreoIa.monitoringCenter.feed")}
        </span>
        {loading ? (
          <span className="ml-auto font-mono text-[9px] max-sm:text-[10px] uppercase tracking-wider text-content-faint">
            {t("monitoreoIa.monitoringCenter.feedConnecting")}
          </span>
        ) : !unavailable ? (
          <span className="ml-auto flex items-center gap-1.5 font-mono text-[9px] max-sm:text-[10px] uppercase tracking-wider text-success">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
            </span>
            {t("monitoreoIa.monitoringCenter.online")}
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading ? (
          <TabLoader label={t("monitoreoIa.monitoringCenter.feedLoading")} />
        ) : unavailable ? (
          <EmptyState
            icon="ri-cloud-off-line"
            title={t("monitoreoIa.monitoringCenter.feedUnavailable")}
            description={t("monitoreoIa.monitoringCenter.feedUnavailableHint")}
          />
        ) : cases.length === 0 ? (
          <EmptyState
            icon="ri-shield-check-line"
            title={t("monitoreoIa.monitoringCenter.feedEmpty")}
            description={t("monitoreoIa.monitoringCenter.feedEmptyHint")}
          />
        ) : (
          <ul className="space-y-2.5">
            {cases.map((item) => {
              const live = liveOf?.(item.deviceId) ?? null;
              const active = item.id === activeCaseId;
              const esperando = item.requiresHuman && item.isOpen;

              return (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={!live}
                  onClick={() => onSelectCase(item)}
                  /* El que espera a una persona se resalta: fondo propio y el
                     borde izquierdo más grueso. No basta con subirlo —una lista
                     larga se recorre y el de arriba deja de verse—, y es el
                     único que pide que alguien haga algo. */
                  className={`w-full rounded-r py-0.5 pl-2.5 pr-2 text-left transition-colors ${
                    esperando ? "border-l-4 bg-warning/10" : "border-l-2"
                  } ${
                    active ? "bg-primary-500/10" : live ? "hover:bg-subtle" : "cursor-default"
                  } ${esperando ? "border-l-warning" : URGENCY_BORDERS[item.urgency]}`}
                >
                  <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                    {esperando ? (
                      <span
                        className="shrink-0 font-mono text-[9px] max-sm:text-[10px] font-semibold uppercase tracking-wider text-warning"
                        title={t("monitoreoIa.monitoringCenter.esperandoAlguienHint", {
                          defaultValue:
                            "Centinela ya no puede resolverlo solo: necesita que una persona confirme.",
                        })}
                      >
                        <i className="ri-user-follow-line mr-0.5" />
                        {t("monitoreoIa.monitoringCenter.esperandoAlguien", { defaultValue: "Te toca" })}
                      </span>
                    ) : null}
                    <span
                      className={`font-mono text-[9px] max-sm:text-[10px] uppercase tracking-wider ${
                        URGENCY_TEXT[item.urgency]
                      }`}
                    >
                      {labelOfCase(item)}
                    </span>
                    <span className="ml-auto flex min-w-0 items-center gap-1">
                      {live ? (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: WATCH_STATUS_MAP_COLORS[live.status] }}
                        />
                      ) : null}
                      <span className="truncate font-mono text-[10.5px] text-content-faint">
                        {item.unitName ?? live?.name ?? ""}
                      </span>
                    </span>
                  </span>

                  {item.summary ? (
                    <p className="mt-0.5 line-clamp-3 text-[10.5px] leading-snug text-content-secondary">
                      {item.summary}
                    </p>
                  ) : null}

                  <span className="mt-0.5 flex items-center gap-1.5 font-mono text-[9.5px] text-content-faint">
                    {formatAgo(item.openedAt, t)}
                    {!item.isOpen ? (
                      <span className="text-success">
                        · {t("monitoreoIa.caseEvents.status.resuelto")}
                      </span>
                    ) : null}
                    {item.requiresHuman ? (
                      <span className="text-error">· {t("monitoreoIa.caseEvents.requiresHuman")}</span>
                    ) : null}
                    {!live ? (
                      <span>· {t("monitoreoIa.monitoringCenter.feedNoUnit")}</span>
                    ) : null}

                    {/* ── El atajo al mapa ────────────────────────────────
                        Sólo en el que espera a una persona, y sólo cuando la
                        unidad está en el mapa. Toda la fila ya centra el caso
                        al tocarla, pero eso no se ve: sin un texto que lo
                        diga, quien abre la sección por primera vez no sabe que
                        la lista y el mapa están conectados, y se queda leyendo
                        resúmenes en vez de ir a mirar dónde está el camión.

                        No es otro botón —un botón dentro de otro botón no es
                        HTML válido y además duplicaría la acción—: es la misma
                        fila diciendo a dónde lleva. */}
                    {esperando && live && !active ? (
                      <span className="ml-auto shrink-0 text-warning">
                        {t("monitoreoIa.monitoringCenter.verEnMapa", { defaultValue: "Ver en el mapa" })}
                        <i className="ri-arrow-right-line ml-0.5" />
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="shrink-0 border-t border-line px-3 py-2 text-[10px] max-sm:text-[11px] text-content-faint">
        {t("monitoreoIa.monitoringCenter.feedHint")}
      </p>
    </div>
  );
}
